import React from 'react';
import PropTypes from 'prop-types';

import {
  Alert,
  Box,
  Button,
  Chip,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from 'views/components';
import {SearchOutlined, WarningAmberOutlined} from 'views/icons';
import {Translate, translate} from 'views/module';

import formatters from '../shared/formatters';

import SortableHeaderCell from './SortableHeaderCell';
import {
  compareClients,
  DEFAULT_SORT,
  FLAG_ACCENT_COLOR,
  FLAG_ROW_BACKGROUND,
  getTableColumns,
  getUnpricedHours,
  hasMissingRate,
  isFlaggedClient,
} from './clientMonthlyShared';

const bodyCellSx = {
  fontSize: '12px',
  lineHeight: '16px',
  color: '#1A1916',
  whiteSpace: 'nowrap',
  borderBottom: '1px solid #F2F0EC',
  verticalAlign: 'top',
};

// Backend totals cover every client in the snapshot, so the totals row is shaded and
// weighted to read as a distinct summary rather than one more paginated data row.
const footerCellSx = {
  fontSize: '12px',
  fontWeight: 700,
  lineHeight: '16px',
  color: '#1A1916',
  whiteSpace: 'nowrap',
  backgroundColor: '#F4F1EC',
  borderTop: '2px solid #E0DBD2',
  borderBottom: 'none',
};

const warningChipSx = {
  'height': 22,
  'backgroundColor': FLAG_ROW_BACKGROUND,
  'borderRadius': '20px',
  'color': FLAG_ACCENT_COLOR,
  'fontSize': '11px',
  'fontWeight': 500,
  '& .MuiChip-label': {
    px: 0.75,
  },
};

const renderClientCell = (client) => (
  <Stack spacing={0.25} sx={{minWidth: 0, maxWidth: 260}}>
    <Link
      href={`/client/${client.clientRn}`}
      underline='hover'
      sx={{
        fontSize: '12px',
        fontWeight: 600,
        lineHeight: '16px',
        color: '#1A1916',
      }}
    >
      {client.clientName || client.clientRn}
    </Link>
    <Tooltip title={translate('metrics.ClientMetrics.ClientMonthly.Table.RnTooltip', {rn: client.clientRn})} placement='top-start'>
      <Typography
        sx={{
          fontSize: '10px',
          lineHeight: '13px',
          color: '#C0BBB2',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {client.clientRn}
      </Typography>
    </Tooltip>
  </Stack>
);

const renderMissingRate = () => (
  <Typography component='span' sx={{fontSize: '12px', color: FLAG_ACCENT_COLOR}}>
    <Translate expression='metrics.ClientMetrics.ClientMonthly.Table.MissingRate' />
  </Typography>
);

const renderScheduledRevenueCell = (client) => {
  if (hasMissingRate(client) || formatters.isMissingValue(client.scheduledRevenue)) {
    return (
      <Typography component='span' sx={{fontSize: '12px', color: FLAG_ACCENT_COLOR}}>
        <Translate expression='metrics.ClientMetrics.ClientMonthly.Table.Unpriced' />
      </Typography>
    );
  }

  return formatters.formatCurrency(client.scheduledRevenue);
};

// Data-quality/status column. Warnings carry both an icon and a text label so the state is
// never communicated through colour alone.
const renderStatusCell = (client) => {
  if (hasMissingRate(client)) {
    return (
      <Chip
        icon={<WarningAmberOutlined sx={{fontSize: '14px !important', color: `${FLAG_ACCENT_COLOR} !important`}} />}
        label={translate('metrics.ClientMetrics.ClientMonthly.Table.MissingRate')}
        sx={warningChipSx}
      />
    );
  }

  if (getUnpricedHours(client) > 0) {
    return (
      <Chip
        icon={<WarningAmberOutlined sx={{fontSize: '14px !important', color: `${FLAG_ACCENT_COLOR} !important`}} />}
        label={translate('metrics.ClientMetrics.ClientMonthly.Table.PartlyUnpriced', {hours: formatters.formatHours(client.unpricedScheduledHours)})}
        sx={warningChipSx}
      />
    );
  }

  if (client.clientStatus && client.clientStatus !== 'ACTIVE') {
    return (
      <Chip
        label={client.clientStatus}
        sx={{
          'height': 22,
          'backgroundColor': '#F2F0EC',
          'borderRadius': '20px',
          'color': '#919191',
          'fontSize': '11px',
          'fontWeight': 500,
          '& .MuiChip-label': {
            px: 0.75,
          },
        }}
      />
    );
  }

  // A priced client with no scheduled hours is data-complete, so it stays on the neutral
  // palette (never red) while still being called out as having nothing scheduled.
  if (!formatters.isMissingValue(client.scheduledHours) && client.scheduledHours === 0) {
    return (
      <Typography component='span' sx={{fontSize: '11px', color: '#A8A49E'}}>
        <Translate expression='metrics.ClientMetrics.ClientMonthly.Table.NoScheduledHours' />
      </Typography>
    );
  }

  return (
    <Typography component='span' sx={{fontSize: '11px', color: '#A8A49E'}}>
      <Translate expression='metrics.ClientMetrics.ClientMonthly.Table.Priced' />
    </Typography>
  );
};

const renderBodyCell = (client, column) => {
  switch (column.key) {
    case 'client':
      return renderClientCell(client);
    case 'scheduledRate':
      return hasMissingRate(client) ? renderMissingRate() : formatters.formatCurrency(client.scheduledRate);
    case 'scheduledHours':
      return formatters.formatHours(client.scheduledHours);
    case 'scheduledWeightedHours':
      return formatters.formatHours(client.scheduledWeightedHours);
    case 'scheduledRevenue':
      return renderScheduledRevenueCell(client);
    case 'actualHours':
      return formatters.formatHours(client.actualHours);
    case 'actualRevenue':
      return formatters.formatCurrency(client.actualRevenue);
    case 'actualRate':
      return formatters.formatRateList(client.actualRatesApplied);
    case 'status':
      return renderStatusCell(client);
    default:
      return null;
  }
};

// Totals are the nested backend aggregates: totals.scheduled.* (billing side) and
// totals.actual.* (may be null for the next month).
const renderFooterCell = (column, totals) => {
  const scheduled = totals?.scheduled || null;
  const actual = totals?.actual || null;
  const unpricedHours = scheduled?.unpricedClientHours;

  switch (column.key) {
    case 'client':
      return (
        <Stack spacing={0.25}>
          <Typography sx={{fontSize: '12px', fontWeight: 700, lineHeight: '16px', color: '#1A1916'}}>
            <Translate expression='metrics.ClientMetrics.ClientMonthly.Table.TotalsRow' />
          </Typography>
          <Typography sx={{fontSize: '10px', lineHeight: '13px', color: '#A8A49E'}}>
            <Translate expression='metrics.ClientMetrics.ClientMonthly.Table.TotalsNote' />
          </Typography>
        </Stack>
      );
    case 'scheduledHours':
      return formatters.formatHours(scheduled?.clientHours);
    case 'scheduledWeightedHours':
      return formatters.formatHours(scheduled?.clientWeightedHours);
    case 'scheduledRevenue':
      return formatters.formatCurrency(scheduled?.revenue);
    case 'actualHours':
      return formatters.formatHours(actual?.clientHours);
    case 'actualRevenue':
      return formatters.formatCurrency(actual?.revenue);
    case 'status':
      return !formatters.isMissingValue(unpricedHours) && unpricedHours > 0 ?
        translate('metrics.ClientMetrics.ClientMonthly.Table.PartlyUnpriced', {hours: formatters.formatHours(unpricedHours)}) :
        formatters.unavailableText();
    default:
      return formatters.unavailableText();
  }
};

const ClientMonthlyClientsTable = (props) => {
  const {
    clients,
    totals,
    showActual,
    hasMore,
    isLoadingMore,
    errorMessage,
    onLoadMore,
  } = props;

  const [sortState, setSortState] = React.useState(DEFAULT_SORT);
  const [searchTerm, setSearchTerm] = React.useState('');

  const columns = React.useMemo(() => getTableColumns(showActual), [showActual]);

  const handleSort = (sortKey) => {
    setSortState((currentSort) => {
      if (currentSort.key === sortKey) {
        return {key: sortKey, direction: currentSort.direction === 'asc' ? 'desc' : 'asc'};
      }

      return {key: sortKey, direction: sortKey === 'clientName' ? 'asc' : 'desc'};
    });
  };

  const visibleClients = React.useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const filtered = term ?
      clients.filter((client) => {
        return (client.clientName || '').toLowerCase().includes(term) ||
          (client.clientRn || '').toLowerCase().includes(term);
      }) :
      clients;

    return [...filtered].sort((left, right) => compareClients(left, right, sortState.key, sortState.direction));
  }, [clients, searchTerm, sortState]);

  return (
    <Paper
      sx={{
        p: 3,
        borderRadius: '12px',
        border: '1px solid #E5E5E5',
        boxShadow: 'none',
        minWidth: 0,
      }}
    >
      <Stack
        direction={{xs: 'column', sm: 'row'}}
        justifyContent='space-between'
        alignItems={{xs: 'flex-start', sm: 'center'}}
        spacing={1.5}
        sx={{mb: 2}}
      >
        <Typography sx={{fontSize: '12px', color: '#A8A49E'}}>
          <Translate
            expression='metrics.ClientMetrics.ClientMonthly.Table.ResultCount'
            substitute={{shown: visibleClients.length, total: clients.length}}
          />
        </Typography>
        <TextField
          value={searchTerm}
          onChange={setSearchTerm}
          icon={SearchOutlined}
          size='small'
          placeholder={translate('metrics.ClientMetrics.ClientMonthly.Table.SearchPlaceholder')}
          aria-label={translate('metrics.ClientMetrics.ClientMonthly.Table.SearchPlaceholder')}
          sx={{width: {xs: '100%', sm: 260}}}
        />
      </Stack>

      <TableContainer sx={{overflowX: 'auto'}}>
        <Table size='small' sx={{minWidth: showActual ? 920 : 620}}>
          <TableHead>
            <TableRow>
              {
                columns.map((column) => (
                  <SortableHeaderCell key={column.key} column={column} sortState={sortState} onSort={handleSort} />
                ))
              }
            </TableRow>
          </TableHead>
          <TableBody>
            {
              visibleClients.map((client) => {
                const flagged = isFlaggedClient(client);

                return (
                  <TableRow
                    key={client.clientRn}
                    sx={{backgroundColor: flagged ? FLAG_ROW_BACKGROUND : 'transparent'}}
                  >
                    {
                      columns.map((column) => (
                        <TableCell key={column.key} align={column.align} sx={bodyCellSx}>
                          {renderBodyCell(client, column)}
                        </TableCell>
                      ))
                    }
                  </TableRow>
                );
              })
            }
            <TableRow>
              {
                columns.map((column) => (
                  <TableCell key={column.key} align={column.align} sx={footerCellSx}>
                    {renderFooterCell(column, totals)}
                  </TableCell>
                ))
              }
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Typography sx={{fontSize: '11px', lineHeight: '15px', color: '#A8A49E', mt: 1.5}}>
        <Translate expression='metrics.ClientMetrics.ClientMonthly.Table.TotalsCaption' />
      </Typography>

      {
        errorMessage &&
        <Alert severity='warning' sx={{mt: 2}}>
          {errorMessage}
        </Alert>
      }

      {
        hasMore &&
        <Box sx={{mt: 2, display: 'flex', justifyContent: 'center'}}>
          <Button
            variant='outlined'
            disabled={isLoadingMore}
            onClick={onLoadMore}
            sx={{
              'height': '34px',
              'border': '1px solid #DCDDE5',
              'borderRadius': '7px',
              'color': '#6B6761',
              'textTransform': 'none',
              'fontSize': '12px',
              '&:hover': {
                border: '1px solid #C0BBB2',
              },
            }}
          >
            <Translate expression='metrics.ClientMetrics.ClientMonthly.LoadMore' />
          </Button>
        </Box>
      }
    </Paper>
  );
};

ClientMonthlyClientsTable.propTypes = {
  clients: PropTypes.array.isRequired,
  totals: PropTypes.object,
  showActual: PropTypes.bool.isRequired,
  hasMore: PropTypes.bool,
  isLoadingMore: PropTypes.bool,
  errorMessage: PropTypes.string,
  onLoadMore: PropTypes.func,
};

export default React.memo(ClientMonthlyClientsTable);
